import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  FileSignature,
  CheckCircle,
  AlertTriangle,
  XCircle,
  DollarSign,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import signedContractService from '../../services/signedContractService'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const SignedContract = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalContracts: 0,
    activeContracts: 0,
    expiredContracts: 0,
    terminatedContracts: 0,
    suspendedContracts: 0,
    renewedContracts: 0,
    totalContractValue: 0,
    activeContractValue: 0,
    expiringSoon: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [contractsResponse, statsResponse] = await Promise.all([
        signedContractService.getAllSignedContracts(),
        signedContractService.getSignedContractStats()
      ])
      
      if (contractsResponse.success) {
        const transformedData = (contractsResponse.data || []).map(contract => ({
          ...contract,
          id: contract._id
        }))
        setData(transformedData)
        if (contractsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No signed contract records found. Sign a draft contract to create your first signed contract!')
        }
      } else {
        toast.error(contractsResponse.message || 'Failed to load signed contracts')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading signed contract records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view signed contracts')
      } else if (!error._errorHandled) {
        toast.error('Failed to load signed contract records')
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Active': 'active',
      'Expired': 'inactive',
      'Terminated': 'danger',
      'Suspended': 'warning',
      'Renewed': 'active',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const isExpiringSoon = (endDate) => {
    if (!endDate) return false
    const today = new Date()
    const end = new Date(endDate)
    const diffTime = end - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 && diffDays <= 30
  }

  const columns = [
    {
      accessorKey: 'contractNumber',
      header: 'Contract Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contractTitle',
      header: 'Contract Title',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'partyName',
      header: 'Party',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.partyCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.partyCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'signedDate',
      header: 'Signed Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'contractEndDate',
      header: 'End Date',
      cell: ({ getValue }) => {
        const date = getValue()
        const expiring = isExpiringSoon(date)
        return (
          <div style={{ textAlign: 'center', color: expiring ? '#dc3545' : '#495057', fontWeight: expiring ? '600' : 'normal' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
            {expiring && <div style={{ fontSize: '11px', color: '#dc3545' }}>Expiring Soon</div>}
          </div>
        )
      },
    },
    {
      accessorKey: 'contractValue',
      header: 'Contract Value',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/signed-contract/form')
  }

  const handleEdit = (contractData) => {
    navigate('/contracts/signed-contract/edit', { state: { contractData } })
  }

  const handleView = (contractData) => {
    navigate('/contracts/signed-contract/view', { state: { contractData } })
  }

  const handleDelete = (contractData) => {
    setDeleteItem(contractData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting signed contract...')
      const response = await signedContractService.deleteSignedContract(deleteItem._id)
      if (response.success) {
        toast.success('Signed contract deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete signed contract')
      }
    } catch (error) {
      console.error('Error deleting signed contract:', error)
      if (error.response?.status === 404) {
        toast.error('Signed contract not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this signed contract')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete signed contract. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Signed Contracts', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Signed Contracts</h1>
          <p>Executed and active contracts</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Contract
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <FileSignature size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalContracts || 0}</div>
          <div className="metric-title">Total Contracts</div>
          <div className="metric-subtitle">All signed contracts</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeContracts || 0}</div>
          <div className="metric-title">Active Contracts</div>
          <div className="metric-subtitle">Currently in force</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Soon</span>
            </div>
          </div>
          <div className="metric-value">{stats.expiringSoon || 0}</div>
          <div className="metric-title">Expiring Soon</div>
          <div className="metric-subtitle">Within 30 days</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Expired</span>
            </div>
          </div>
          <div className="metric-value">{stats.expiredContracts || 0}</div>
          <div className="metric-title">Expired</div>
          <div className="metric-subtitle">Past end date</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalContractValue)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">All signed contracts</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.activeContractValue)}</div>
          <div className="metric-title">Active Value</div>
          <div className="metric-subtitle">Active contracts only</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="signed-contract-table"
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Signed Contract"
        message={`Are you sure you want to delete signed contract "${deleteItem?.contractNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default SignedContract
